import { ChatOutlined } from '@mui/icons-material';
import { gridDateComparator, gridNumberComparator, gridStringOrNumberComparator } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctNegotiationCaseStatus } from '@/generated/gql/graphql';
import { I18nNamespace, NegotiationDemandDataGridField } from '~/common/types';
import {
  cellFactory,
  ChipCell,
  CurrencyCell,
  CurrencyCellValue,
  DataGridConfig,
  DataGridLinkVariant,
  DateRangeCell,
  DateRangeCellValue,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  gridColumnGroupFactory,
  GridSortDirectionType,
  IssueChipCell,
  LinkCell,
  LinkCellValue,
  NegotiationStatusChipCell,
  NumberCell,
  numberFormatterFactory,
  StatusCheckCell,
  StatusCheckCellValue,
  TooltipCell,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { IconCell } from '~/components/DataGridCustom/cells/IconCell/IconCell';
import { NegotiationProcessStatusChipCell } from '~/components/DataGridCustom/cells/NegotiationProcessStatusChipCell/NegotiationProcessStatusChipCell';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { NegotiationCaseIsReleasableCellMenu } from '~/components/NegotiationCaseIsReleasableCellMenu/NegotiationCaseIsReleasableCellMenu';
import { RouteId } from '~/routes';
import { isNegotiatedQuote } from '~/util/types/isNegotiatedQuote';

import {
  DemandCasesDataGridField,
  DemandCasesDataGridColumnGroup,
  DemandCasesDataGridRow,
} from './DemandCasesWidget.types';

export const useDemandCasesDataGridConfig: UseDataGridConfig<DemandCasesDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tGroup } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandCasesWidget.columnGroup' });
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandCasesWidget.column' });
  const { t: tCell } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandCasesWidget.cell' });
  const { t: tQuoteEditMode } = useTranslation(undefined, { keyPrefix: 'QuoteEditMode' });
  const { t: tNegotiationMode } = useTranslation(undefined, { keyPrefix: 'NegotiationMode' });
  const { entityId: demandId } = useModuleContext();

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    const createGroup = gridColumnGroupFactory({ tGroup });

    return {
      sortModel: [{ field: NegotiationDemandDataGridField.SEQUENCE_NUMBER, sort: GridSortDirectionType.DESC }],
      columnVisibilityModel: {
        [DemandCasesDataGridField.STATUS]: false,
        [DemandCasesDataGridField.HAS_SUPPLIER_COMMENT]: false,
        [DemandCasesDataGridField.LATEST_COMMENT]: false,
      },
      columns: [
        createColumn({
          field: DemandCasesDataGridField.CASE_NUMBER,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.negotiationDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 200,
          hideable: false,
          sortComparator: (v1, v2, cellParams1, cellParams2) => {
            return gridStringOrNumberComparator(v1?.label, v2?.label, cellParams1, cellParams2);
          },
        }),
        createColumn({
          field: DemandCasesDataGridField.SUPPLIER,
          minWidth: 350,
          renderCell: cellFactory(CuriosityValue, ({ row }) => ({
            ns: CuriosityNamespaceType.SUPPLIER,
            i18nKey: row.supplier.internalId,
          })),
          sortable: false,
        }),
        createColumn({
          field: DemandCasesDataGridField.VARIANT,
          minWidth: 200,
          valueGetter: (value: DemandCasesDataGridRow['variant']) => tCell(`addedBy.${value}`),
        }),
        createColumn({
          field: DemandCasesDataGridField.PROCESS_STATUS,
          minWidth: 300,
          renderCell: cellFactory(NegotiationProcessStatusChipCell, ({ row }) => ({
            processStatus: row.processStatus,
            status: row.status,
          })),
        }),
        createColumn({
          field: DemandCasesDataGridField.HAS_SUPPLIER_COMMENT,
          renderCell: cellFactory(IconCell, ({ value }) => ({
            icon: value ? <ChatOutlined /> : undefined,
          })),
          valueGetter: (_value, row) => {
            return row.quote?.hasSupplierComment ?? false;
          },
          minWidth: 150,
        }),
        createColumn({
          field: DemandCasesDataGridField.IS_RELEASEABLE,
          renderCell: cellFactory(StatusCheckCell, ({ row }) => ({
            renderList: () => (
              <NegotiationCaseIsReleasableCellMenu
                list={row.processSteps}
                negotiationCaseId={row.id}
                demandId={demandId}
              />
            ),
          })),
          valueGetter: (value: DemandCasesDataGridRow['isReleasable'], row): StatusCheckCellValue => {
            return row.status === TsrctNegotiationCaseStatus.INITIALIZED ? value : null;
          },
          sortable: false,
          minWidth: 300,
        }),
        createColumn({
          field: DemandCasesDataGridField.STATUS,
          minWidth: 300,
          renderCell: cellFactory(NegotiationStatusChipCell),
        }),
        createColumn({
          field: DemandCasesDataGridField.TOTAL_VALUE,
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
          valueGetter: (_value, row): CurrencyCellValue => {
            if (row.quote) {
              return isNegotiatedQuote(row.quote) ? row.quote.totalDiscountedValue : row.quote.totalValue;
            }
            return null;
          },
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            isMasked: row.isMaskedBidding,
            valueStrikeThrough: row.quote?.totalValue ?? 0,
            currency: row.quote?.currency,
          })),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.TOTAL_SAVINGS_PERCENTAGE,
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(NumberCell, ({ row }) => ({
            preset: NumberFormatPreset.PERCENT_DECIMAL,
            isMasked: row.isMaskedBidding,
          })),
          sortComparator: gridNumberComparator,
          valueGetter: (_value, row) => {
            if (row.quote && isNegotiatedQuote(row.quote)) {
              return row.quote.discount;
            }
            return null;
          },
        }),
        createColumn({
          field: DemandCasesDataGridField.DELIVERY_TIME,
          width: 175,
          minWidth: 150,
          renderCell: cellFactory(DateRangeCell),
          valueGetter: (_value, row): DateRangeCellValue => ({
            from: row.quote?.shipmentTerms?.earlyDeliveryAt,
            to: row.quote?.shipmentTerms?.lateDeliveryAt,
          }),
          sortComparator: (v1, v2, cellParams1, cellParams2) => {
            return gridDateComparator(v1?.to, v2?.to, cellParams1, cellParams2);
          },
        }),
        createColumn({
          field: DemandCasesDataGridField.CONTACT_FIRST_NAME,
          valueGetter: (_value, row) => row.supplier?.contact?.firstName,
          minWidth: 200,
          renderCell: cellFactory(EmptyFallbackCell),
          sortComparator: gridStringOrNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.CONTACT_LAST_NAME,
          valueGetter: (_value, row) => row.supplier?.contact?.lastName,
          minWidth: 200,
          renderCell: cellFactory(EmptyFallbackCell),
          sortComparator: gridStringOrNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.CONTACT_EMAIL,
          valueGetter: (_value, row) => row.supplier?.contact?.email,
          minWidth: 200,
          renderCell: cellFactory(EmptyFallbackCell),
          sortComparator: gridStringOrNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.CONTACT_PHONE,
          valueGetter: (_value, row) => row.supplier?.contact?.phone,
          minWidth: 200,
          renderCell: cellFactory(EmptyFallbackCell),
          sortComparator: gridStringOrNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.UPPER_DISCOUNT_BOUNDARY,
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          sortComparator: gridNumberComparator,
          valueGetter: (_value, row) => row.upperDiscountBoundary,
        }),
        createColumn({
          field: DemandCasesDataGridField.LOWER_DISCOUNT_BOUNDARY,
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          sortComparator: gridNumberComparator,
          valueGetter: (_value, row) => row.lowerDiscountBoundary,
        }),
        createColumn({
          field: DemandCasesDataGridField.NEGOTIATION_MODE,
          minWidth: 250,
          valueGetter: (_value, row) => row.options && tNegotiationMode(row.options.negotiationMode),
          renderCell: cellFactory(ChipCell),
          sortComparator: gridStringOrNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.QUOTE_MODE,
          minWidth: 200,
          valueGetter: (_value, row) => row.options && tQuoteEditMode(row.options.quoteMode),
          renderCell: cellFactory(ChipCell),
          sortComparator: gridStringOrNumberComparator,
        }),
        createColumn({
          field: DemandCasesDataGridField.ISSUES_OPEN_COUNT,
          valueGetter: (_value, row) => row.issueSummary?.openCount,
          renderCell: cellFactory(IssueChipCell),
          minWidth: 150,
        }),
        createColumn({
          field: DemandCasesDataGridField.LATEST_COMMENT,
          minWidth: 250,
          renderCell: cellFactory(TooltipCell),
          valueGetter: (_value, row) => row.latestComment?.message,
          sortComparator: gridStringOrNumberComparator,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: DemandCasesDataGridColumnGroup.CASE,
          children: [
            //
            { field: DemandCasesDataGridField.CASE_NUMBER },
            { field: DemandCasesDataGridField.SUPPLIER },
            { field: DemandCasesDataGridField.VARIANT },
            { field: DemandCasesDataGridField.PROCESS_STATUS },
            { field: DemandCasesDataGridField.IS_RELEASEABLE },
            { field: DemandCasesDataGridField.HAS_SUPPLIER_COMMENT },
            { field: DemandCasesDataGridField.STATUS },
            { field: DemandCasesDataGridField.TOTAL_VALUE },
            { field: DemandCasesDataGridField.TOTAL_SAVINGS_PERCENTAGE },
            { field: DemandCasesDataGridField.DELIVERY_TIME },
          ],
        }),
        createGroup({
          groupId: DemandCasesDataGridColumnGroup.CONTACT,
          children: [
            //
            { field: DemandCasesDataGridField.CONTACT_FIRST_NAME },
            { field: DemandCasesDataGridField.CONTACT_LAST_NAME },
            { field: DemandCasesDataGridField.CONTACT_EMAIL },
            { field: DemandCasesDataGridField.CONTACT_PHONE },
          ],
        }),
        createGroup({
          groupId: DemandCasesDataGridColumnGroup.TERMS,
          children: [
            //
            { field: DemandCasesDataGridField.UPPER_DISCOUNT_BOUNDARY },
            { field: DemandCasesDataGridField.LOWER_DISCOUNT_BOUNDARY },
            { field: DemandCasesDataGridField.QUOTE_MODE },
            { field: DemandCasesDataGridField.NEGOTIATION_MODE },
          ],
        }),
        createGroup({
          groupId: DemandCasesDataGridColumnGroup.ISSUES,
          children: [
            //
            { field: DemandCasesDataGridField.ISSUES_OPEN_COUNT },
            { field: DemandCasesDataGridField.LATEST_COMMENT },
          ],
        }),
      ],
    } as DataGridConfig<DemandCasesDataGridRow>;
  }, [tColumn, t, tGroup, tCell, tNegotiationMode, tQuoteEditMode, demandId]);
};
